import { z } from 'zod';

import { NotificationCountsSchema, NotificationInboxItemSchema } from './notification_inbox';

/**
 * Inbox push fan-out payload: the conversation service publishes it on the
 * outbound SNS topic with the `{channel: 'app'}` message attribute, the
 * EXISTING filter policy routes it to the app_chat_outbound SQS queue, and
 * app-integration broadcasts it over the user's SSE stream. No new infra —
 * the inbox is a projection, not a delivery channel (`app` stays out of
 * `NotificationChannelSchema` by construction).
 *
 * `kind: 'notification.inbox'` is the STRUCTURAL discriminator: the BFF
 * outbound handler already disambiguates frames by shape (messages vs
 * resolutions — see `AppChatPushSchema` / `AppChatResolutionPushSchema`);
 * this literal lets it branch with one cheap check before the full parse.
 *
 * `contactRef.externalId` is the Cognito `sub` — the app-channel addressing
 * rule (the identity binding's `channelUserId` for channel 'app'). It is a
 * narrowed shape on purpose, NOT the conversation `ContactRefSchema`: the
 * channel is already fixed by the SNS attribute, so carrying it again would
 * only invite drift.
 *
 * Cross-service wire contract, not a persisted document — no `_schemaVersion`
 * (same style as `OutboundNotification`); `v: 1` versions the frame instead.
 */
export const NotificationInboxQueueEventSchema = z
  .object({
    v: z.literal(1),
    kind: z.literal('notification.inbox'),
    contactRef: z.object({ externalId: z.string().min(1) }).strict(),
    notification: NotificationInboxItemSchema,
    counts: NotificationCountsSchema,
  })
  .strict();
export type NotificationInboxQueueEvent = z.infer<typeof NotificationInboxQueueEventSchema>;
